import { supabase } from '../../lib/supabase';
import type { DocumentRequestStatus, DocumentStatus } from '../../shared/lib/document-requests';

import type { EventColor } from './activity-format';
import type { AutoRule, CompletionPolicy, FieldKind, RuleConfig } from './checklist';
import type { CourtFilter, StageKind } from './workflow-board';

/**
 * RPC surface for phases 145–148: checklist packs, card checklists, board
 * templates, saved views and custom fields.
 *
 * Same untyped escape hatch as `workflow-api.ts` — the generated
 * `src/types/database.ts` predates all of it.
 */
interface UnknownRpc {
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message: string } | null }>;
}
const rpcUnknown = supabase as unknown as UnknownRpc;

async function call<T>(fn: string, args: Record<string, unknown>, fallback: T): Promise<T> {
  const { data, error } = await rpcUnknown.rpc(fn, args);
  if (error !== null) throw new Error(error.message);
  return (data ?? fallback) as T;
}

async function callVoid(fn: string, args: Record<string, unknown>): Promise<void> {
  const { error } = await rpcUnknown.rpc(fn, args);
  if (error !== null) throw new Error(error.message);
}

export const CHECKLIST_KEYS = {
  packs: (instituteId: string) => ['institute', 'checklist_packs', instituteId] as const,
  pack: (packId: string) => ['institute', 'checklist_pack', packId] as const,
  stage: (stageId: string) => ['institute', 'stage_checklist', stageId] as const,
  card: (cardId: string) => ['institute', 'card_detail', cardId] as const,
  templates: (instituteId: string) => ['institute', 'board_templates', instituteId] as const,
  views: (boardId: string) => ['institute', 'board_views', boardId] as const,
};

/* ------------------------------------------------------- 145: the library -- */

export interface PackSummary {
  id: string;
  code: string | null;
  name: string;
  description: string | null;
  applies_to: 'course' | 'application' | 'both';
  version: number;
  is_system: boolean;
  archived_at: string | null;
  item_count: number;
  required_count: number;
  /** Used on N stages across M boards — the number that makes someone pause. */
  stage_count: number;
  board_count: number;
}

export interface PackItem {
  id: string;
  label: string;
  hint: string | null;
  position: number;
  required: boolean;
  auto_rule: AutoRule;
  auto_config: RuleConfig;
  due_offset_days: number | null;
  /* ---- phase 151 ---- */
  /** Default owner, copied onto each card. Null = whoever owns the card. */
  assignee_id: string | null;
  assignee_name: string | null;
  /* ---- phase B ---- */
  /** Who closes it, copied onto each card the same way the owner is. */
  completion_policy: CompletionPolicy;
}

export interface PackDetail {
  pack: {
    id: string;
    code: string | null;
    name: string;
    description: string | null;
    applies_to: 'course' | 'application' | 'both';
    version: number;
    is_system: boolean;
    archived_at: string | null;
    can_configure: boolean;
  };
  items: PackItem[];
  used_by: { stage_id: string; stage_name: string; board_id: string; board_name: string }[];
}

export function listPacks(instituteId: string): Promise<PackSummary[]> {
  return call<PackSummary[]>(
    'institute_list_checklist_packs',
    { p_institute_id: instituteId, p_include_archived: false },
    [],
  );
}

export async function getPack(packId: string): Promise<PackDetail> {
  const data = await call<PackDetail | null>(
    'institute_get_checklist_pack',
    {
      p_pack_id: packId,
    },
    null,
  );
  if (data === null) throw new Error('no pack');
  return data;
}

export async function upsertPack(input: {
  pack_id: string | null;
  institute_id: string;
  name: string;
  description: string | null;
  applies_to: 'course' | 'application' | 'both';
}): Promise<string> {
  const data = await call<{ pack_id?: string }>(
    'institute_upsert_checklist_pack',
    {
      p_pack_id: input.pack_id,
      p_institute_id: input.institute_id,
      p_name: input.name,
      p_description: input.description,
      p_applies_to: input.applies_to,
    },
    {},
  );
  return data.pack_id ?? '';
}

export function archivePack(packId: string, archived: boolean): Promise<void> {
  return callVoid('institute_archive_checklist_pack', {
    p_pack_id: packId,
    p_archived: archived,
  });
}

export function upsertPackItem(input: {
  item_id: string | null;
  pack_id: string;
  label: string;
  hint: string | null;
  required: boolean;
  auto_rule: AutoRule;
  auto_config: RuleConfig;
  due_offset_days: number | null;
  assignee_id: string | null;
  completion_policy: CompletionPolicy;
}): Promise<void> {
  return callVoid('institute_upsert_pack_item', {
    p_item_id: input.item_id,
    p_pack_id: input.pack_id,
    p_label: input.label,
    p_hint: input.hint,
    p_required: input.required,
    p_auto_rule: input.auto_rule,
    p_auto_config: input.auto_config,
    p_due_offset_days: input.due_offset_days,
    p_assignee_id: input.assignee_id,
    p_completion_policy: input.completion_policy,
  });
}

export function deletePackItem(itemId: string): Promise<void> {
  return callVoid('institute_delete_pack_item', { p_item_id: itemId });
}

/** Takes the complete order, so a fast double-click cannot half-apply. */
export function reorderPackItems(packId: string, itemIds: string[]): Promise<void> {
  return callVoid('institute_reorder_pack_items', {
    p_pack_id: packId,
    p_item_ids: itemIds,
  });
}

/* ----------------------------------------------- 145: the stage attachment -- */

export interface StagePackItem {
  id: string;
  label: string;
  hint: string | null;
  required: boolean;
  auto_rule: AutoRule;
  auto_config: RuleConfig;
  due_offset_days: number | null;
  /** EFFECTIVE, not inherited: what a card landing on this stage will get. */
  assignee_id: string | null;
  assignee_name: string | null;
  completion_policy: CompletionPolicy;
  /** Phase J — this stage has its own answer for who does this line. */
  overridden: boolean;
  hidden: boolean;
}

export interface StagePack {
  pack_id: string;
  name: string;
  description: string | null;
  version: number;
  position: number;
  hidden_item_ids: string[];
  items: StagePackItem[];
}

export interface StageExtraItem {
  id: string;
  label: string;
  hint: string | null;
  position: number;
  required: boolean;
  auto_rule: AutoRule;
  auto_config: RuleConfig;
  due_offset_days: number | null;
  assignee_id: string | null;
  assignee_name: string | null;
  completion_policy: CompletionPolicy;
}

/** `request_documents` (phase 174) asks the student for every document item on the card. */
export type RuleAction =
  'assign_member' | 'set_due' | 'attach_pack' | 'log_event' | 'notify' | 'request_documents';

export interface StageRule {
  id: string;
  trigger: 'on_enter' | 'on_ready';
  action: RuleAction;
  config: Record<string, unknown>;
  enabled: boolean;
  position: number;
}

export interface StageChecklist {
  packs: StagePack[];
  extras: StageExtraItem[];
  rules: StageRule[];
}

export async function getStageChecklist(stageId: string): Promise<StageChecklist> {
  const data = await call<StageChecklist | null>(
    'institute_get_stage_checklist',
    { p_stage_id: stageId },
    null,
  );
  return data ?? { packs: [], extras: [], rules: [] };
}

export function attachPack(
  stageId: string,
  packId: string,
  applyToExisting: boolean,
): Promise<void> {
  return callVoid('institute_attach_pack_to_stage', {
    p_stage_id: stageId,
    p_pack_id: packId,
    p_apply_to_existing: applyToExisting,
  });
}

/** `copyItems` forks the pack's items onto this stage — "detach & customise". */
export function detachPack(stageId: string, packId: string, copyItems: boolean): Promise<void> {
  return callVoid('institute_detach_pack_from_stage', {
    p_stage_id: stageId,
    p_pack_id: packId,
    p_copy_items: copyItems,
  });
}

/**
 * Who does an inherited line, HERE (phase J).
 *
 * `inherit` deletes the override rather than writing a null-shaped one, so
 * "put it back the way the checklist has it" is a real state and not a second
 * spelling of "the card's owner does it".
 */
export function setStageItemOwner(input: {
  stage_id: string;
  pack_item_id: string;
  inherit: boolean;
  assignee_id: string | null;
  completion_policy: CompletionPolicy;
}): Promise<void> {
  return callVoid('institute_set_stage_item_owner', {
    p_stage_id: input.stage_id,
    p_pack_item_id: input.pack_item_id,
    p_inherit: input.inherit,
    p_assignee_id: input.assignee_id,
    p_completion_policy: input.completion_policy,
  });
}

export function setHiddenItems(stageId: string, packId: string, hidden: string[]): Promise<void> {
  return callVoid('institute_set_stage_hidden_items', {
    p_stage_id: stageId,
    p_pack_id: packId,
    p_hidden: hidden,
  });
}

export function upsertStageExtraItem(input: {
  item_id: string | null;
  stage_id: string;
  label: string;
  hint: string | null;
  required: boolean;
  auto_rule: AutoRule;
  auto_config: RuleConfig;
  due_offset_days: number | null;
  assignee_id: string | null;
  completion_policy: CompletionPolicy;
}): Promise<void> {
  return callVoid('institute_upsert_stage_extra_item', {
    p_item_id: input.item_id,
    p_stage_id: input.stage_id,
    p_label: input.label,
    p_hint: input.hint,
    p_required: input.required,
    p_auto_rule: input.auto_rule,
    p_auto_config: input.auto_config,
    p_due_offset_days: input.due_offset_days,
    p_assignee_id: input.assignee_id,
    p_completion_policy: input.completion_policy,
  });
}

export function deleteStageExtraItem(itemId: string): Promise<void> {
  return callVoid('institute_delete_stage_extra_item', { p_item_id: itemId });
}

export function saveStageChecklistAsPack(stageId: string, name: string): Promise<void> {
  return callVoid('institute_save_stage_checklist_as_pack', {
    p_stage_id: stageId,
    p_name: name,
    p_replace: true,
  });
}

export function upsertStageRule(input: {
  rule_id: string | null;
  stage_id: string;
  action: RuleAction;
  config: Record<string, unknown>;
  enabled: boolean;
}): Promise<void> {
  return callVoid('institute_upsert_stage_rule', {
    p_rule_id: input.rule_id,
    p_stage_id: input.stage_id,
    p_trigger: 'on_enter',
    p_action: input.action,
    p_config: input.config,
    p_enabled: input.enabled,
  });
}

export function deleteStageRule(ruleId: string): Promise<void> {
  return callVoid('institute_delete_stage_rule', { p_rule_id: ruleId });
}

/* -------------------------------------------------------- 145: the card -- */

/**
 * The live document request behind a `document_uploaded` item (phase 174),
 * with its latest submission so the drawer can review it in place.
 */
export interface ItemRequest {
  id: string;
  status: DocumentRequestStatus;
  due_at: string | null;
  requested_at: string;
  last_reminded_at: string | null;
  submitted_at: string | null;
  resolved_at: string | null;
  rejection_reason: string | null;
  rejected_count: number;
  document_id: string | null;
  document_label: string | null;
  document_status: DocumentStatus | null;
  storage_path: string | null;
  external_url: string | null;
}

export interface CardChecklistItem {
  id: string;
  source_kind: 'pack' | 'stage_extra' | 'ad_hoc';
  pack_id: string | null;
  pack_name: string | null;
  label: string;
  hint: string | null;
  position: number;
  required: boolean;
  auto_rule: AutoRule;
  auto_config: RuleConfig;
  due_at: string | null;
  assignee_id: string | null;
  assignee_name: string | null;
  note: string | null;
  blocked_reason: string | null;
  blocked_until: string | null;
  done_at: string | null;
  done_source: 'manual' | 'auto' | 'system' | 'student' | null;
  done_by_name: string | null;
  /** Null unless the student has been asked for this document. */
  request: ItemRequest | null;
  /**
   * Phase 179b — null unless this line has been handed to the student.
   *
   * NOT the item's own state. `status: 'done'` with `done_source: 'student'`
   * means they CLAIMED it; the item above stays open until staff tick it, and
   * nothing a student does writes to a workflow table
   * (docs/MESSAGING_PLAN.md D4).
   */
  task: ItemTask | null;
  /** Phase 180 — who closes this line. */
  completion_policy: CompletionPolicy;
}

/**
 * Who closes a checklist line — declared in `checklist.ts` (the pure module the
 * owner helpers live in) and re-exported here so every existing importer of
 * this file keeps working.
 *
 * A line whose auto_rule is `document_uploaded` is asked for as a phase 174
 * document REQUEST instead — that mechanism already exists and closes when
 * staff accept the file, so it is deliberately not a fourth policy.
 */
export type { CompletionPolicy } from './checklist';

export interface ItemTask {
  id: string;
  status: 'open' | 'done' | 'cancelled';
  due_at: string | null;
  created_at: string;
  done_at: string | null;
  done_source: 'student' | 'staff' | 'auto' | null;
  thread_id: string | null;
  /** How many times staff sent it back after the student marked it done. */
  reopened_count: number;
  completion_policy: 'student_signals' | 'student_closes';
}

export interface CardField {
  id: string;
  key: string;
  label: string;
  kind: FieldKind;
  options: string[];
  text: string | null;
  num: number | null;
  date: string | null;
  bool: boolean | null;
  user_id: string | null;
  user_name: string | null;
}

export interface CardVisit {
  visit_id: number;
  stage_name: string;
  moved_at: string;
  reason: string;
  by_name: string | null;
}

export interface CardDetail {
  card: {
    id: string;
    board_id: string;
    stage_id: string;
    stage_name: string;
    stage_color: EventColor;
    stage_kind: StageKind;
    auto_advance: boolean;
    student_id: string;
    student_name: string;
    roll_no: string | null;
    exam_date: string | null;
    /** The student's own shared folder (Drive, Dropbox, …), if one is set. */
    documents_url: string | null;
    /** Files the student sent that nobody has reviewed yet. */
    pending_review: number;
    /** Documents the student has been asked for and not yet sent. */
    open_requests: number;
    due_at: string | null;
    assignee_id: string | null;
    assignee_name: string | null;
    entered_stage_at: string;
    visit_id: number | null;
    can_configure: boolean;
  };
  checklist: CardChecklistItem[];
  fields: CardField[];
  history: CardVisit[];
}

export async function getCardDetail(cardId: string): Promise<CardDetail> {
  const data = await call<CardDetail | null>(
    'institute_get_card_detail',
    { p_card_id: cardId },
    null,
  );
  if (data === null) throw new Error('no card');
  return data;
}

export interface TickResult {
  changed: boolean;
  /** Set when ticking this item completed the stage and the card advanced. */
  moved_to_stage_id: string | null;
}

export function tickChecklistItem(itemId: string, done: boolean): Promise<TickResult> {
  return call<TickResult>(
    'institute_tick_checklist_item',
    { p_item_id: itemId, p_done: done },
    { changed: false, moved_to_stage_id: null },
  );
}

export function updateChecklistItem(input: {
  item_id: string;
  assignee_id: string | null;
  due_at: string | null;
  note: string | null;
  blocked_reason: string | null;
  blocked_until: string | null;
}): Promise<void> {
  return callVoid('institute_update_checklist_item', {
    p_item_id: input.item_id,
    p_assignee_id: input.assignee_id,
    p_due_at: input.due_at,
    p_note: input.note,
    p_blocked_reason: input.blocked_reason,
    p_blocked_until: input.blocked_until,
  });
}

export interface AddCardItemInput {
  cardId: string;
  label: string;
  required: boolean;
  dueAt: string | null;
  /** Default 'manual'. A `document_uploaded` rule needs `autoConfig.kind`. */
  autoRule?: AutoRule;
  autoConfig?: RuleConfig;
  /** With a document rule: raise the request to the student straight away. */
  askStudent?: boolean;
}

export function addCardChecklistItem(input: AddCardItemInput): Promise<void> {
  return callVoid('institute_add_card_checklist_item', {
    p_card_id: input.cardId,
    p_label: input.label,
    p_required: input.required,
    p_due_at: input.dueAt,
    p_auto_rule: input.autoRule ?? 'manual',
    p_auto_config: input.autoConfig ?? {},
    p_ask_student: input.askStudent ?? false,
  });
}

/** Ask the student for every open document item on the card, in one go. */
export function requestMissingDocuments(cardId: string): Promise<{ requested: number }> {
  return call<{ requested: number }>(
    'institute_request_missing_documents',
    { p_card_id: cardId },
    { requested: 0 },
  );
}

export function deleteCardChecklistItem(itemId: string): Promise<void> {
  return callVoid('institute_delete_card_checklist_item', { p_item_id: itemId });
}

/* ------------------------------------------------------- 146: advancing -- */

export function advanceReadyCards(stageId: string): Promise<{ moved: number }> {
  return call<{ moved: number }>(
    'institute_advance_ready_cards',
    { p_stage_id: stageId },
    { moved: 0 },
  );
}

/* ------------------------------------------------- 147: board templates -- */

export interface BoardTemplateSummary {
  id: string;
  code: string | null;
  name: string;
  description: string | null;
  is_system: boolean;
  stage_count: number;
  stage_names: string[];
}

export function listBoardTemplates(instituteId: string): Promise<BoardTemplateSummary[]> {
  return call<BoardTemplateSummary[]>(
    'institute_list_board_templates',
    { p_institute_id: instituteId },
    [],
  );
}

export function saveBoardAsTemplate(boardId: string, name: string): Promise<void> {
  return callVoid('institute_save_board_as_template', {
    p_board_id: boardId,
    p_name: name,
  });
}

export function deleteBoardTemplate(templateId: string): Promise<void> {
  return callVoid('institute_delete_board_template', { p_template_id: templateId });
}

export async function duplicateBoard(
  boardId: string,
  name: string,
  withCards: boolean,
): Promise<string> {
  const data = await call<{ board_id?: string }>(
    'institute_duplicate_board',
    { p_board_id: boardId, p_name: name, p_with_cards: withCards },
    {},
  );
  return data.board_id ?? '';
}

/* ---------------------------------------------------- 147: saved views -- */

/** `flow` (phase 151) is the pipeline canvas — the same board, wired up. */
export type ViewMode = 'board' | 'flow' | 'table' | 'calendar' | 'assignee';

export interface ViewFilters {
  search?: string;
  stage_ids?: string[];
  assignee_ids?: string[];
  overdue_only?: boolean;
  ready_only?: boolean;
  stale_only?: boolean;
  /**
   * Phase F — whose move it is. Single-valued, unlike the booleans above:
   * "who is this waiting on" has one answer at a time. Saved views carry it in
   * the same jsonb blob, and an older view that predates it simply has no key.
   */
  court?: CourtFilter | undefined;
}

export interface SavedView {
  id: string;
  name: string;
  mode: ViewMode;
  filters: ViewFilters;
  shared: boolean;
  is_mine: boolean;
  position: number;
}

export function listBoardViews(boardId: string): Promise<SavedView[]> {
  return call<SavedView[]>('institute_list_board_views', { p_board_id: boardId }, []);
}

export function saveBoardView(input: {
  view_id: string | null;
  board_id: string;
  name: string;
  mode: ViewMode;
  filters: ViewFilters;
  shared: boolean;
}): Promise<void> {
  return callVoid('institute_save_board_view', {
    p_view_id: input.view_id,
    p_board_id: input.board_id,
    p_name: input.name,
    p_mode: input.mode,
    p_filters: input.filters,
    p_shared: input.shared,
  });
}

export function deleteBoardView(viewId: string): Promise<void> {
  return callVoid('institute_delete_board_view', { p_view_id: viewId });
}

/* ---------------------------------------------------- 148: board fields -- */

export function upsertBoardField(input: {
  field_id: string | null;
  board_id: string;
  key: string;
  label: string;
  kind: FieldKind;
  options: string[];
}): Promise<void> {
  return callVoid('institute_upsert_board_field', {
    p_field_id: input.field_id,
    p_board_id: input.board_id,
    p_key: input.key,
    p_label: input.label,
    p_kind: input.kind,
    p_options: input.options,
  });
}

export function deleteBoardField(fieldId: string): Promise<void> {
  return callVoid('institute_delete_board_field', { p_field_id: fieldId });
}

/**
 * One value, typed. Every slot is a separate argument rather than one blob, so
 * a type mistake surfaces at this boundary instead of as a cast failure inside
 * a plpgsql function three layers down.
 */
export function setCardField(input: {
  card_id: string;
  field_id: string;
  text?: string | null;
  num?: number | null;
  date?: string | null;
  bool?: boolean | null;
  user_id?: string | null;
}): Promise<void> {
  return callVoid('institute_set_card_field', {
    p_card_id: input.card_id,
    p_field_id: input.field_id,
    p_text: input.text ?? null,
    p_num: input.num ?? null,
    p_date: input.date ?? null,
    p_bool: input.bool ?? null,
    p_user_id: input.user_id ?? null,
  });
}
